import { MatterTreatmentBillContextMenu } from "@/components/features/matter/context-menus/MatterTreatmentBillContextMenu";
import { useUser } from "@/components/UserProvider";
import { DataTable } from "@/components/ui/shadcn/DataTable/DataTable";
import type { MedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMedicalTreatmentBill } from "@/lib/data/react-query/matters/medical-treatment-bill";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { trimText } from "@/lib/utils/format";
import { useLocation } from "@tanstack/react-router";
import type { Row } from "@tanstack/react-table";
import { createMatterTreatmentBillsColumns } from "./matter-treatment-bills-columns";

interface MatterTreatmentBillsDataTableProps {
  bills: MedicalTreatmentBillViewModel[];
  matter: Matter;
  tableButtons?: React.ReactNode;
}

export const MatterTreatmentBillsDataTable = ({
  bills,
  matter,
  tableButtons,
}: MatterTreatmentBillsDataTableProps) => {
  const { openTreatmentBillModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const deleteBill = useDeleteMedicalTreatmentBill();
  const location = useLocation();
  const canDeleteBills = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill,
    ACTION_PERMISSION_VALUES.delete,
  );

  // Check if we're on the settlement page
  const isOnSettlementPage = location.pathname.includes("/settlements");
  const isOnLiensPage = location.pathname.includes("/liens");

  // Get columns with conditional additional fields when on settlements page
  const columns = createMatterTreatmentBillsColumns(
    isOnSettlementPage,
    isOnLiensPage,
  );

  const handleRowClick = (row: Row<MedicalTreatmentBillViewModel>) => {
    openTreatmentBillModal({
      matterId: matter.id,
      billId: row.original.id,
      treatmentId: row.original.medicalTreatmentId,
      editing: true,
      initialTab: "details",
    });
  };

  return (
    <>
      <DataTable
        entityLabelPlural="bills"
        columns={columns}
        data={bills}
        onRowClick={handleRowClick}
        renderRowContextMenu={(row, trigger) => {
          const bill = row.original;
          const label = trimText(bill.recordLabel) || "-";

          return (
            <MatterTreatmentBillContextMenu
              canDelete={canDeleteBills}
              id={bill.id}
              isDeleting={deleteBill.isPending}
              label={label}
              matter={matter}
              onDelete={async () => {
                await deleteBill.mutateAsync({
                  matterId: matter.id,
                  id: bill.id,
                });
              }}
              onEdit={() => {
                handleRowClick(row);
              }}
              originalBill={bill.originalBill}
              status={bill.recordStatus}
              trigger={trigger}
            />
          );
        }}
        tableName="medical-treatment-bills-table"
        tableButtons={tableButtons}
      />
    </>
  );
};
